import { useCallback, useEffect, useState } from "react";
import { Globe, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, errorText } from "@/lib/api";

const SNIPPET = `<script src="${window.location.origin}/ceres-embed.js" defer></script>`;

const AddSite = ({ onSaved }) => {
  const [domain, setDomain] = useState("");
  const [subdomains, setSubdomains] = useState(true);
  const submit = async (e) => {
    e.preventDefault();
    try {
      onSaved((await http.post("/admin/embed-sites", { domain, subdomains })).data);
      setDomain("");
      toast("Website added. Ceres can be embedded there now.");
    } catch (ex) { toast.error(errorText(ex.response?.data?.detail)); }
  };
  return (
    <form onSubmit={submit} className="mt-5 flex flex-wrap items-end gap-4" data-testid="embed-site-add-form">
      <label className="block flex-1 text-[11px] font-semibold uppercase tracking-wider text-hv-muted">
        Domain
        <input required value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" data-testid="embed-site-domain-input"
          className="mt-1 w-full border border-hv-rule px-2 py-1.5 text-[13px] normal-case tracking-normal text-hv-text focus:border-hv-navy focus:outline-none" />
      </label>
      <label className="flex items-center gap-2 pb-2 text-[13px] text-hv-text">
        <Switch checked={subdomains} onCheckedChange={setSubdomains} data-testid="embed-site-subdomains-new" /> Include subdomains
      </label>
      <button type="submit" className="hv-btn-light hv-btn-sm" data-testid="embed-site-add-button"><Plus size={14} /> Add website</button>
    </form>
  );
};

const SiteRow = ({ s, onSaved }) => {
  const act = async (fn, msg) => {
    try { onSaved((await fn()).data); toast(msg); } catch (ex) { toast.error(errorText(ex.response?.data?.detail)); }
  };
  const remove = () => window.confirm(`Stop Ceres from loading on ${s.domain}?`) && act(() => http.delete(`/admin/embed-sites/${s.domain}`), "Website removed");
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`embed-site-row-${s.domain}`}>
      <span className="flex items-center gap-2 text-[14px] font-semibold text-hv-text"><Globe size={14} className="text-hv-muted" />{s.domain}
        <span className="text-[12px] font-normal text-hv-muted">{s.subdomains ? "and its subdomains" : "exact domain only"}</span></span>
      <span className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-[12px] text-hv-muted">
          <Switch checked={s.subdomains} data-testid={`embed-site-subdomains-${s.domain}`}
            onCheckedChange={(v) => act(() => http.patch(`/admin/embed-sites/${s.domain}`, { subdomains: v }), "Saved")} /> Subdomains
        </label>
        <button onClick={remove} className="text-hv-muted transition-colors hover:text-red-600" aria-label={`Remove ${s.domain}`} data-testid={`embed-site-remove-${s.domain}`}><Trash2 size={15} /></button>
      </span>
    </li>
  );
};

export const EmbedSitesPanel = () => {
  const [sites, setSites] = useState([]);
  const load = useCallback(() => http.get("/admin/embed-sites").then((r) => setSites(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const copy = () => navigator.clipboard.writeText(SNIPPET).then(() => toast("Snippet copied"));
  return (
    <section className="border border-hv-rule bg-white p-6" data-testid="embed-sites-panel">
      <h2 className="font-heading text-lg font-bold text-hv-navy">Allowed websites</h2>
      <p className="mt-1 text-[13px] text-hv-muted">Ceres only opens on these websites. Changes apply straight away.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3 bg-hv-section px-3 py-2">
        <code className="flex-1 break-all text-[12px] text-hv-text" data-testid="embed-snippet-code">{SNIPPET}</code>
        <button onClick={copy} className="hv-btn-light hv-btn-sm" data-testid="embed-snippet-copy-button">Copy snippet</button>
      </div>
      <ul className="mt-2 divide-y divide-hv-rule" data-testid="embed-sites-list">
        {sites.map((s) => <SiteRow key={s.domain} s={s} onSaved={setSites} />)}
      </ul>
      <AddSite onSaved={setSites} />
    </section>
  );
};
